import React, { useState, useEffect } from 'react';
import { 
  getCertificates,
  addCertificate,
  updateCertificate,
  deleteCertificate
} from '../../services/profile';
import { FaEdit, FaTrashAlt, FaPlus, FaTimes, FaSave, FaAward, FaExternalLinkAlt, FaIdCard } from 'react-icons/fa';
import './Certificates.css';

const Certificates = ({ profileSection, profileData, setCompletion }) => {
  const [certificatesList, setCertificatesList] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isEditing, setIsEditing] = useState(null);
  const [formError, setFormError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // State for new/editing certificate
  const [certificateForm, setCertificateForm] = useState({
    certificate_name: '',
    issuing_organization: '',
    issue_date: '',
    expiration_date: '',
    credential_id: '',
    credential_url: '',
    description: ''
  });

  // Fetch certificates on component mount
  useEffect(() => {
    const fetchCertificates = async () => {
      try {
        setIsLoading(true);
        const response = await getCertificates(profileData?.profile_id);
        setCertificatesList(response);
      } catch (error) {
        console.error('Error fetching certificates:', error);
        setFormError('Failed to load certificates. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCertificates();
  }, []);

  
    useEffect(() => {
      setCompletion("certificates", certificatesList);
    }, [certificatesList])
  

  // Clear form data
  const clearForm = () => {
    setCertificateForm({
      certificate_name: '',
      issuing_organization: '',
      issue_date: '',
      expiration_date: '',
      credential_id: '',
      credential_url: '',
      description: ''
    });
    setFormError('');
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCertificateForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Add new certificate
  const handleAddCertificate = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!certificateForm.certificate_name || !certificateForm.issuing_organization || !certificateForm.issue_date) {
      setFormError('Please fill out all required fields');
      return;
    }
    
    try {
      // Format data for API
      const certificateData = {
        certificate_name: certificateForm.certificate_name,
        issuing_organization: certificateForm.issuing_organization,
        issue_date: certificateForm.issue_date,
        expiration_date: certificateForm.expiration_date || null,
        credential_id: certificateForm.credential_id || null,
        credential_url: certificateForm.credential_url || null,
        description: certificateForm.description || null
      };
      
      // Send to API
      const response = await addCertificate(certificateData, profileData?.profile_id);
      
      // Update local state with response
      setCertificatesList(prev => [...prev, response]);
      
      // Reset form and state
      clearForm();
      setIsAdding(false);
    } catch (error) {
      console.error('Error adding certificate:', error);
      setFormError('Failed to add certificate. Please try again.');
    }
  };

  // Start editing a certificate
  const handleEditStart = (certificate) => {
    setIsEditing(certificate.id);
    
    setCertificateForm({
      certificate_name: certificate.certificate_name,
      issuing_organization: certificate.issuing_organization || '',
      issue_date: certificate.issue_date || '',
      expiration_date: certificate.expiration_date || '',
      credential_id: certificate.credential_id || '',
      credential_url: certificate.credential_url || '',
      description: certificate.description || ''
    });
  };

  // Save edited certificate
  const handleEditSave = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!certificateForm.certificate_name || !certificateForm.issuing_organization || !certificateForm.issue_date) {
      setFormError('Please fill out all required fields');
      return;
    }
    
    try {
      // Format data for API
      const certificateData = {
        certificate_name: certificateForm.certificate_name,
        issuing_organization: certificateForm.issuing_organization,
        issue_date: certificateForm.issue_date,
        expiration_date: certificateForm.expiration_date || null,
        credential_id: certificateForm.credential_id || null,
        credential_url: certificateForm.credential_url || null,
        description: certificateForm.description || null
      };
      
      // Update certificate
      const response = await updateCertificate(isEditing, certificateData, profileData?.profile_id);
      
      // Update the certificate in the local state
      setCertificatesList(prev => 
        prev.map(cert => cert.id === isEditing ? response : cert)
      );
      
      // Reset form and state
      clearForm();
      setIsEditing(null);
    } catch (error) {
      console.error('Error updating certificate:', error);
      setFormError('Failed to update certificate. Please try again.');
    }
  };

  // Cancel adding/editing
  const handleCancel = () => {
    clearForm();
    setIsAdding(false);
    setIsEditing(null);
  };

  // Delete certificate
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this certificate?')) {
      try {
        await deleteCertificate(id, profileData?.profile_id);
        setCertificatesList(prev => prev.filter(cert => cert.id !== id));
      } catch (error) {
        console.error('Error deleting certificate:', error);
        setFormError('Failed to delete certificate. Please try again.');
      }
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="certificates-loading">
        <div className="loading-spinner"></div>
        <p>Loading certificates...</p>
      </div>
    );
  }

  return (
    <div className="certificates-section">
      <div className="section-header">
        <h2><div className="section-icon">
          {profileSection.icon}
        </div> Certificates</h2>
        {!isAdding && !isEditing && (
          <button 
            onClick={() => setIsAdding(true)}
            className="btn-add"
          >
            <FaPlus /> Add Certificate
          </button>
        )}
      </div>
      
      {/* Certificates List */}
      {certificatesList.length > 0 ? (
        <div className="certificates-list">
          {certificatesList.map((certificate) => (
            <div key={certificate.id} className="certificate-item">
              {isEditing === certificate.id ? (
                <form onSubmit={handleEditSave} className="certificate-form">
                  <h3>Edit Certificate</h3>
                  {formError && <div className="form-error">{formError}</div>}
                  
                  <div className="form-group">
                    <label htmlFor="certificate_name">Certificate Name *</label>
                    <input
                      type="text"
                      id="certificate_name"
                      name="certificate_name"
                      value={certificateForm.certificate_name}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="issuing_organization">Issuing Organization *</label>
                    <input
                      type="text"
                      id="issuing_organization"
                      name="issuing_organization"
                      value={certificateForm.issuing_organization}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="issue_date">Issue Date *</label>
                      <input
                        type="date"
                        id="issue_date"
                        name="issue_date"
                        value={certificateForm.issue_date}
                        onChange={handleInputChange}
                        required
                        className="form-control"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="expiration_date">Expiry Date (if applicable)</label>
                      <input
                        type="date"
                        id="expiration_date"
                        name="expiration_date"
                        value={certificateForm.expiration_date}
                        onChange={handleInputChange}
                        className="form-control"
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="credential_id">Credential ID</label>
                    <input
                      type="text"
                      id="credential_id"
                      name="credential_id"
                      value={certificateForm.credential_id}
                      onChange={handleInputChange}
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="credential_url">Credential URL</label>
                    <input
                      type="url"
                      id="credential_url"
                      name="credential_url"
                      value={certificateForm.credential_url}
                      onChange={handleInputChange}
                      placeholder="https://example.com/credential"
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea
                      id="description"
                      name="description"
                      value={certificateForm.description}
                      onChange={handleInputChange}
                      rows="3"
                      className="form-control"
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" onClick={handleCancel} className="btn-secondary">
                      <FaTimes /> Cancel
                    </button>
                    <button type="submit" className="btn-primary">
                      <FaSave /> Save Changes
                    </button>
                  </div>
                </form>
              ) : (
                <div className="certificate-display">
                  <div className="certificate-header">
                    <div className="certificate-title">
                      <h4>{certificate.certificate_name}</h4>
                      <p className="certificate-issuer">{certificate.issuing_organization}</p>
                      <p className="certificate-date">
                        Issued: {formatDate(certificate.issue_date)}
                        {certificate.expiration_date && ` • Expires: ${formatDate(certificate.expiration_date)}`}
                      </p>
                    </div>
                    <div className="certificate-actions">
                      <button 
                        onClick={() => handleEditStart(certificate)}
                        className="btn-icon edit"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(certificate.id)}
                        className="btn-icon delete"
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </div>
                  </div>
                  
                  {(certificate.credential_id || certificate.credential_url || certificate.description) && (
                    <div className="certificate-details">
                      {certificate.credential_id && (
                        <p className="credential-id">
                          <FaIdCard className="detail-icon" /> <strong>Credential ID:</strong> {certificate.credential_id}
                        </p>
                      )}
                      
                      {certificate.credential_url && (
                        <p className="credential-url">
                          <FaExternalLinkAlt className="detail-icon" /> <strong>Credential URL:</strong>{' '}
                          <a href={certificate.credential_url} target="_blank" rel="noopener noreferrer" className="external-link">
                            View Credential
                          </a>
                        </p>
                      )}
                      
                      {certificate.description && (
                        <div className="certificate-description">
                          <strong>Description:</strong>
                          <p>{certificate.description}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-list">
          <p>No certificates added yet.</p>
          <p>Click "Add Certificate" to get started.</p>
        </div>
      )}
      
      {/* Add Certificate Form */}
      {isAdding && (
        <div className="add-certificate">
          <form onSubmit={handleAddCertificate} className="certificate-form">
            <h3>Add New Certificate</h3>
            {formError && <div className="form-error">{formError}</div>}
            
            <div className="form-group">
              <label htmlFor="certificate_name">Certificate Name *</label>
              <input
                type="text"
                id="certificate_name"
                name="certificate_name"
                value={certificateForm.certificate_name}
                onChange={handleInputChange}
                required
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="issuing_organization">Issuing Organization *</label>
              <input
                type="text"
                id="issuing_organization"
                name="issuing_organization"
                value={certificateForm.issuing_organization}
                onChange={handleInputChange}
                required
                className="form-control"
              />
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="issue_date">Issue Date *</label>
                <input
                  type="date"
                  id="issue_date"
                  name="issue_date"
                  value={certificateForm.issue_date}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="expiration_date">Expiry Date (if applicable)</label>
                <input
                  type="date"
                  id="expiration_date"
                  name="expiration_date"
                  value={certificateForm.expiration_date}
                  onChange={handleInputChange}
                  className="form-control"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="credential_id">Credential ID</label>
              <input
                type="text"
                id="credential_id"
                name="credential_id"
                value={certificateForm.credential_id}
                onChange={handleInputChange}
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="credential_url">Credential URL</label>
              <input
                type="url"
                id="credential_url"
                name="credential_url"
                value={certificateForm.credential_url}
                onChange={handleInputChange}
                placeholder="https://example.com/credential"
                className="form-control"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                name="description"
                value={certificateForm.description}
                onChange={handleInputChange}
                rows="3"
                className="form-control"
              />
            </div>
            
            <div className="form-actions">
              <button type="button" onClick={handleCancel} className="btn-secondary">
                <FaTimes /> Cancel
              </button>
              <button type="submit" className="btn-primary">
                <FaPlus /> Add Certificate
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default Certificates;